import React, { useState, useEffect, useCallback } from 'react';
import { HardDrive, Folder, File, Share2, Search, Upload, FolderPlus, RefreshCw, Trash2, ChevronRight, Grid, List, X, Download, Eye, MoreVertical, Tag, Plus, Maximize2, FileText, Lock, Recycle, FolderOpen } from 'lucide-react';
import { saveAs } from 'file-saver';
import Sidebar from '../components/ui/Sidebar';
import {
  CreateBucketModal,
  CreateFolderModal,
  DeleteConfirmModal,
  FilePreviewModal
} from '../components/storage';
import { storageService } from '../../services/storageService';
import type { Breadcrumb, Bucket, BucketInfoResponse, FileSystemItem, StorageObject } from '../../types/storage';

// Tab types
type StorageTab = 'all' | 'folders' | 'shared-with-me' | 'shared-with-others' | 'secure' | 'recycle';

const StoragePage: React.FC = () => {
  // State
  const [buckets, setBuckets] = useState<Bucket[]>([]);
  const [currentBucket, setCurrentBucket] = useState<string>('');
  const [currentPath, setCurrentPath] = useState<string>('/');
  const [breadcrumbs, setBreadcrumbs] = useState<Breadcrumb[]>([]);
  const [items, setItems] = useState<FileSystemItem[]>([]);
  const [selectedFile, setSelectedFile] = useState<FileSystemItem | null>(null);
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('grid');
  const [searchQuery, setSearchQuery] = useState<string>('');

  const [activeTab, setActiveTab] = useState<StorageTab>('all');

  // Loading states
  const [bucketsLoading, setBucketsLoading] = useState(true);
  const [contentLoading, setContentLoading] = useState(false);

  // Modal states
  const [showCreateBucketModal, setShowCreateBucketModal] = useState(false);
  const [showCreateFolderModal, setShowCreateFolderModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDeleteBucketModal, setShowDeleteBucketModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isPdfPreview, setIsPdfPreview] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Stats
  const [stats, setStats] = useState({
    totalSize: '0 GB',
    totalBytes: 0,
    maxBytes: 15 * 1024 * 1024 * 1024, // 15 GB
    usedPercentage: 0,
    foldersCount: 0,
    filesCount: 0,
    sharedCount: 0
  });



  // Format file size
  const formatSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} Gb`;
  };

  // Get file type
  const getFileType = (filename: string): 'folder' | 'file' => {
    const extension = filename.split('.').pop()?.toLowerCase();
    return extension && extension !== filename ? 'file' : 'folder';
  };

  // Convert API response to file system structure
  const convertToFileSystem = useCallback((data: BucketInfoResponse, bucketName: string): { [key: string]: FileSystemItem[] } => {
    const fs: { [key: string]: FileSystemItem[] } = { '/': [] };
    const seenFolders = new Set<string>();
    let totalFiles = 0;
    let totalFolders = 0;
    let totalBytes = 0;

    data.objects.forEach((item: StorageObject) => {
      const pathParts = item.name.split('/');
      const file = pathParts.pop() || '';
      const fileName = file === '' ? (item.contentType === 'directory' ? file : pathParts.pop() || '') : file;
      let currPath = '/';

      for (let i = 0; i < pathParts.length; i++) {
        const folderName = pathParts[i];
        const folderPath = currPath === '/' ? `/${folderName}` : `${currPath}/${folderName}`;

        if (!seenFolders.has(folderPath)) {
          if (!fs[currPath]) fs[currPath] = [];

          if (!fs[currPath].some(x => x.name === folderName && x.type === 'folder')) {
            fs[currPath].push({
              name: folderName,
              type: 'folder',
              lastModified: item.lastModified ? item.lastModified.split('T')[0] : null,
              size: formatSize(0),
              bucketName: bucketName
            });
            totalFolders++;
          }

          seenFolders.add(folderPath);
          fs[folderPath] = [];
        }

        currPath = folderPath;
      }

      if (!fs[currPath]) fs[currPath] = [];

      if (fileName) {
        const fileType = getFileType(fileName);
        fs[currPath].push({
          name: fileName,
          type: fileType,
          lastModified: item.lastModified ? item.lastModified.split('T')[0] : null,
          size: formatSize(item.size),
          fileSize: item.size,
          bucketName: bucketName,
          contentType: item.contentType,
          etag: item.etag,
          last_modified_date: item.lastModified,
          legalHoldStatus: item.legalHoldStatus,
          metadata: item.metadata,
          retentionDuration: item.retentionDuration,
          retentionMode: item.retentionMode,
          tags: item.tags,
          versionId: item.versionId
        });

        if (fileType === 'file') {
          totalFiles++;
          totalBytes += item.size;
        }
      }
    });

    setStats(prev => ({
      ...prev,
      foldersCount: totalFolders,
      filesCount: totalFiles,
      totalSize: formatSize(totalBytes),
      totalBytes: totalBytes,
      usedPercentage: Math.min((totalBytes / prev.maxBytes) * 100, 100)
    }));

    return fs;
  }, []);

  // Fetch buckets
  const fetchBuckets = useCallback(async (selectBucket?: string) => {
    setBucketsLoading(true);
    try {
      const bucketNames = await storageService.getBuckets();
      const bucketList: Bucket[] = bucketNames.map((name, index) => ({
        name,
        selected: selectBucket ? name === selectBucket : (currentBucket === '' ? index === 0 : name === currentBucket)
      }));
      setBuckets(bucketList);

      const selectedBucket = bucketList.find(b => b.selected);
      if (selectedBucket) {
        handleBucketSelect(selectedBucket.name);
      }
    } catch (error: unknown) {
      console.error("Can't load buckets");
    } finally {
      setBucketsLoading(false);
    }
  }, [currentBucket]);

  // Fetch bucket info
  const fetchBucketInfo = useCallback(async (bucketName: string) => {
    setContentLoading(true);
    setSelectedFile(null);
    setCurrentPath('/');
    setBreadcrumbs([{ label: bucketName, path: '/' }]);

    try {
      const data = await storageService.getBucketInfo(bucketName);
      const fs = convertToFileSystem(data, bucketName);
      setItems(fs['/'] || []);
    } catch (error: unknown) {
      console.error("Can't load bucket contents");
      setItems([]);
    } finally {
      setContentLoading(false);
    }
  }, [convertToFileSystem]);

  // Fetch folder info
  const fetchFolderInfo = useCallback(async (bucketName: string, folderPath: string) => {
    setContentLoading(true);
    setSelectedFile(null);

    try {
      const data = await storageService.getFolderInfo(bucketName, folderPath);
      const fs = convertToFileSystem(data, bucketName);
      setItems(fs[currentPath] || []);
    } catch (error: unknown) {
      console.error("Can't load folder contents");
      setItems([]);
    } finally {
      setContentLoading(false);
    }
  }, [convertToFileSystem, currentPath]);

  // Initial load
  useEffect(() => {
    fetchBuckets();
  }, []);

  // Handle bucket selection
  const handleBucketSelect = useCallback((bucketName: string) => {
    setCurrentBucket(bucketName);
    setBuckets(prev => prev.map(b => ({ ...b, selected: b.name === bucketName })));
    fetchBucketInfo(bucketName);
  }, [fetchBucketInfo]);

  // Handle item click
  const handleItemClick = useCallback((item: FileSystemItem) => {
    if (item.type === 'folder') {
      const newPath = currentPath === '/' ? `/${item.name}` : `${currentPath}/${item.name}`;
      setCurrentPath(newPath);

      const parts = newPath.split('/').filter(Boolean);
      setBreadcrumbs([
        { label: currentBucket, path: '/' },
        ...parts.map((part, idx) => ({
          label: part,
          path: '/' + parts.slice(0, idx + 1).join('/')
        }))
      ]);

      fetchFolderInfo(currentBucket, newPath.replace('/', ''));
    } else {
      setSelectedFile(item);
    }
  }, [currentPath, currentBucket, fetchFolderInfo]);

  // Handle breadcrumb click
  const handleBreadcrumbClick = useCallback((path: string) => {
    if (path === '/') {
      fetchBucketInfo(currentBucket);
    } else {
      setCurrentPath(path);
      const parts = path.split('/').filter(Boolean);
      setBreadcrumbs([
        { label: currentBucket, path: '/' },
        ...parts.map((part, idx) => ({
          label: part,
          path: '/' + parts.slice(0, idx + 1).join('/')
        }))
      ]);
      fetchFolderInfo(currentBucket, path.replace('/', ''));
    }
    setSelectedFile(null);
  }, [currentBucket, fetchBucketInfo, fetchFolderInfo]);

  // Handle search
  const handleSearch = useCallback(async (query: string) => {
    setSearchQuery(query);
    if (query.length >= 3) {
      setContentLoading(true);
      try {
        const results = await storageService.searchFiles(query);
        const transformedResults: FileSystemItem[] = results.map((item: { objectPath: string; lastModified?: string; size: number; bucketName: string }) => ({
          name: item.objectPath,
          type: getFileType(item.objectPath),
          lastModified: item.lastModified?.split('T')[0] || null,
          size: formatSize(item.size),
          bucketName: item.bucketName
        }));
        setItems(transformedResults);
      } catch {
        setItems([]);
      } finally {
        setContentLoading(false);
      }
    }
  }, []);

  // Clear search
  const handleClearSearch = useCallback(() => {
    setSearchQuery('');
    if (currentPath === '/') {
      fetchBucketInfo(currentBucket);
    } else {
      fetchFolderInfo(currentBucket, currentPath.replace('/', ''));
    }
  }, [currentBucket, currentPath, fetchBucketInfo, fetchFolderInfo]);

  // Handle file upload
  const handleFileUpload = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const file = files[0];
    const folderPath = breadcrumbs.slice(1).map(b => b.label).join('/');

    try {
      await storageService.uploadFile(file, currentBucket, folderPath, '');
      if (currentPath === '/') {
        fetchBucketInfo(currentBucket);
      } else {
        fetchFolderInfo(currentBucket, currentPath.replace('/', ''));
      }
    } catch {
      console.error('Upload failed');
    }

    event.target.value = '';
  }, [breadcrumbs, currentBucket, currentPath, fetchBucketInfo, fetchFolderInfo]);

  // Handle download
  const handleDownload = useCallback(async () => {
    if (!selectedFile) return;

    const objectName = currentPath === '/'
      ? `/${selectedFile.name}`
      : `${currentPath}/${selectedFile.name}`;

    try {
      const { data, headers } = await storageService.downloadFile(currentBucket, objectName);
      const contentDisposition = headers['content-disposition'];
      let filename = selectedFile.name;

      if (contentDisposition) {
        const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (match) {
          filename = match[1].replace(/['"]/g, '');
        }
      }

      saveAs(data, filename);
    } catch {
      console.error('Download failed');
    }
  }, [currentBucket, currentPath, selectedFile]);

  // Handle preview
  const handlePreview = useCallback(async () => {
    if (!selectedFile) return;

    const objectName = currentPath === '/'
      ? `/${selectedFile.name}`
      : `${currentPath}/${selectedFile.name}`;

    try {
      const { data, headers } = await storageService.downloadFile(currentBucket, objectName);
      const contentType = headers['content-type'] || 'application/octet-stream';
      const blob = new Blob([data], { type: contentType });

      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result as string);
        setIsPdfPreview(contentType.includes('pdf'));
        setShowPreviewModal(true);
      };
      reader.readAsDataURL(blob);
    } catch {
      console.error('Preview failed');
    }
  }, [currentBucket, currentPath, selectedFile]);

  // Handle delete file
  const handleDeleteFile = useCallback(async () => {
    if (!selectedFile) return;

    setIsDeleting(true);
    const objectName = currentPath === '/'
      ? selectedFile.name
      : `${currentPath}/${selectedFile.name}`.replace(/^\//, '');

    try {
      await storageService.deleteFiles(currentBucket, [objectName]);
      setSelectedFile(null);
      setShowDeleteModal(false);

      if (currentPath === '/') {
        fetchBucketInfo(currentBucket);
      } else {
        fetchFolderInfo(currentBucket, currentPath.replace('/', ''));
      }
    } catch {
      console.error('Delete failed');
    } finally {
      setIsDeleting(false);
    }
  }, [currentBucket, currentPath, selectedFile, fetchBucketInfo, fetchFolderInfo]);

  // Handle create bucket
  const handleCreateBucket = useCallback(async (bucketName: string) => {
    try {
      await storageService.createBucket(bucketName);
      fetchBuckets(bucketName);
    } catch {
      console.error('Create bucket failed');
    }
  }, [fetchBuckets]);

  // Handle delete bucket
  const handleDeleteBucket = useCallback(async () => {
    setIsDeleting(true);
    try {
      await storageService.deleteBucket(currentBucket);
      setShowDeleteBucketModal(false);
      fetchBuckets();
    } catch {
      console.error('Delete bucket failed');
    } finally {
      setIsDeleting(false);
    }
  }, [currentBucket, fetchBuckets]);

  // Handle refresh
  const handleRefresh = useCallback(() => {
    if (currentPath === '/') {
      fetchBucketInfo(currentBucket);
    } else {
      fetchFolderInfo(currentBucket, currentPath.replace('/', ''));
    }
  }, [currentBucket, currentPath, fetchBucketInfo, fetchFolderInfo]);

  // Check if file is previewable
  const isPreviewable = (file: FileSystemItem) => {
    const validTypes = ['.pdf', '.png', '.jpg', '.jpeg', '.gif', '.webp', '.svg'];
    return validTypes.some(type => file.name.toLowerCase().endsWith(type));
  };

  // Get file icon based on extension
  const getFileTypeIcon = (filename: string) => {
    const ext = filename.split('.').pop()?.toLowerCase();
    if (['doc', 'docx'].includes(ext || '')) return 'W';
    if (['pdf'].includes(ext || '')) return 'pdf';
    if (['ppt', 'pptx'].includes(ext || '')) return 'P';
    if (['xls', 'xlsx'].includes(ext || '')) return 'X';
    return 'F';
  };

  const getFileIconColor = (filename: string) => {
    const ext = filename.split('.').pop()?.toLowerCase();
    if (['doc', 'docx'].includes(ext || '')) return 'bg-blue-600';
    if (['pdf'].includes(ext || '')) return 'bg-red-600';
    if (['ppt', 'pptx'].includes(ext || '')) return 'bg-orange-600';
    if (['xls', 'xlsx'].includes(ext || '')) return 'bg-green-600';
    return 'bg-gray-600';
  };

  // File upload ref
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  // Get folders for display
  const folders = items.filter(item => item.type === 'folder');
  const files = items.filter(item => item.type === 'file');

  // Sidebar Navigation Items (similar to StudioPage)
  const storageNavItems: { key: StorageTab, label: string, icon: React.ReactNode }[] = [
    { key: 'all', label: 'All Activity', icon: <HardDrive size={18} /> },
    { key: 'folders', label: 'My Folders', icon: <Folder size={18} /> },
    { key: 'shared-with-me', label: 'Shared with me', icon: <Share2 size={18} /> },
    { key: 'shared-with-others', label: 'Shared with others', icon: <Share2 size={18} /> },
    { key: 'secure', label: 'Secure Folder', icon: <Lock size={18} /> },
    { key: 'recycle', label: 'Recycle Bin', icon: <Recycle size={18} /> },
  ];

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <Sidebar />

      <div className="flex-1 ml-16 flex">
        {/* Storage Left Navigation - Similar to StudioPage */}
        <div className="w-52 bg-white border-r border-gray-100 py-6 flex-shrink-0">
          <div className="px-4 mb-6">
            <h2 className="text-lg font-semibold text-gray-900">Storage</h2>
            <p className="text-xs text-gray-500 mt-1">{stats.totalSize} of 15GB used</p>
          </div>
          <nav className="space-y-1 px-3">
            {storageNavItems.map((item) => {
              const isActive = item.key === activeTab;
              return (
                <button
                  key={item.key}
                  onClick={() => setActiveTab(item.key)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-lg transition-colors ${isActive
                    ? 'bg-indigo-50 text-indigo-700'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    }`}
                >
                  <span className={isActive ? 'text-indigo-600' : 'text-gray-400'}>
                    {item.icon}
                  </span>
                  {item.label}
                </button>
              );
            })}
          </nav>

          {/* Storage Usage Bar */}
          <div className="mt-6 px-4">
            <div className="bg-gray-100 rounded-full h-2 overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-indigo-500 to-purple-500"
                style={{ width: `${stats.usedPercentage}%` }}
              />
            </div>
            <p className="text-xs text-gray-500 mt-2">{Math.round(stats.usedPercentage)}% used</p>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 overflow-auto bg-gray-50">
          <div className="p-6">
            {/* Content based on active tab */}
            {activeTab === 'folders' ? (
              /* My Folders Tab - Original File Browser */
              <>
                {/* Buckets Bar */}
                <div className="bg-white rounded-lg border border-gray-200 p-4 mb-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-gray-900">Buckets</h3>
                    <button
                      onClick={() => setShowCreateBucketModal(true)}
                      className="flex items-center gap-1 text-sm text-indigo-600 hover:text-indigo-700"
                    >
                      <Plus size={16} />
                      New Bucket
                    </button>
                  </div>
                  <div className="flex gap-2 overflow-x-auto pb-2">
                    {bucketsLoading ? (
                      <div className="flex gap-2">
                        {[1, 2, 3].map(i => (
                          <div key={i} className="h-9 w-24 bg-gray-100 rounded-lg animate-pulse"></div>
                        ))}
                      </div>
                    ) : buckets.map(bucket => (
                      <button
                        key={bucket.name}
                        onClick={() => handleBucketSelect(bucket.name)}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${bucket.selected
                          ? 'bg-indigo-100 text-indigo-700 border border-indigo-200'
                          : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border border-gray-200'
                          }`}
                      >
                        <Folder size={16} className={bucket.selected ? 'text-indigo-600' : 'text-gray-400'} />
                        {bucket.name}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Breadcrumbs & Actions Bar */}
                <div className="bg-white rounded-lg border border-gray-200 p-4 mb-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      {/* Breadcrumbs */}
                      <div className="flex items-center gap-1 text-sm">
                        {breadcrumbs.map((crumb, index) => (
                          <React.Fragment key={crumb.path}>
                            {index > 0 && <ChevronRight size={14} className="text-gray-400" />}
                            <button
                              onClick={() => handleBreadcrumbClick(crumb.path)}
                              className={`px-2 py-1 rounded hover:bg-gray-100 ${index === breadcrumbs.length - 1 ? 'text-indigo-700 font-medium' : 'text-gray-600'
                                }`}
                            >
                              {crumb.label}
                            </button>
                          </React.Fragment>
                        ))}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="relative">
                        <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                        <input
                          type="text"
                          placeholder="Search files..."
                          value={searchQuery}
                          onChange={(e) => handleSearch(e.target.value)}
                          className="pl-10 pr-4 py-2 w-48 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:bg-white"
                        />
                        {searchQuery && (
                          <button onClick={handleClearSearch} className="absolute right-2 top-1/2 -translate-y-1/2">
                            <X size={14} className="text-gray-400" />
                          </button>
                        )}
                      </div>
                      <button onClick={handleRefresh} className="p-2 hover:bg-gray-100 rounded-lg">
                        <RefreshCw size={16} className="text-gray-500" />
                      </button>
                      <button onClick={() => setShowDeleteBucketModal(true)} className="p-2 hover:bg-red-50 rounded-lg">
                        <Trash2 size={16} className="text-gray-500 hover:text-red-600" />
                      </button>
                      <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1 ml-2">
                        <button
                          onClick={() => setViewMode('list')}
                          className={`p-1.5 rounded ${viewMode === 'list' ? 'bg-white shadow-sm' : ''}`}
                        >
                          <List size={16} className={viewMode === 'list' ? 'text-indigo-600' : 'text-gray-500'} />
                        </button>
                        <button
                          onClick={() => setViewMode('grid')}
                          className={`p-1.5 rounded ${viewMode === 'grid' ? 'bg-white shadow-sm' : ''}`}
                        >
                          <Grid size={16} className={viewMode === 'grid' ? 'text-indigo-600' : 'text-gray-500'} />
                        </button>
                      </div>
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700"
                      >
                        <Upload size={16} />
                        Upload
                      </button>
                    </div>
                  </div>
                </div>

                {/* Files List/Grid */}
                <div className="bg-white rounded-lg border border-gray-200">
                  {contentLoading ? (
                    <div className="flex justify-center items-center py-16">
                      <div className="animate-spin rounded-full h-8 w-8 border-2 border-indigo-600 border-t-transparent"></div>
                    </div>
                  ) : items.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-16 text-gray-500">
                      <Folder size={48} className="text-gray-300 mb-4" />
                      <p className="text-sm">This folder is empty</p>
                    </div>
                  ) : viewMode === 'list' ? (
                    <>
                      {/* Table Header */}
                      <div className="p-4 border-b border-gray-200 bg-gray-50">
                        <div className="grid grid-cols-12 gap-4 text-xs font-medium text-gray-500 uppercase tracking-wider">
                          <div className="col-span-5">Name</div>
                          <div className="col-span-2">Type</div>
                          <div className="col-span-2">Modified</div>
                          <div className="col-span-2">Size</div>
                          <div className="col-span-1"></div>
                        </div>
                      </div>

                      {/* Items List */}
                      <div className="divide-y divide-gray-100">
                        {items.map((item, index) => (
                          <div
                            key={`${item.name}-${index}`}
                            onClick={() => handleItemClick(item)}
                            className={`p-4 hover:bg-gray-50 cursor-pointer ${selectedFile?.name === item.name ? 'bg-indigo-50' : ''}`}
                          >
                            <div className="grid grid-cols-12 gap-4 items-center">
                              <div className="col-span-5 flex items-center gap-3">
                                {item.type === 'folder' ? (
                                  <Folder size={24} className="text-amber-500" />
                                ) : (
                                  <File size={24} className="text-gray-500" />
                                )}
                                <div>
                                  <div className="text-sm font-medium text-gray-900">{item.name}</div>
                                  {item.tags && Object.keys(item.tags).length > 0 && (
                                    <div className="flex items-center gap-2 mt-1">
                                      {Object.entries(item.tags).slice(0, 2).map(([key, value]) => (
                                        <span key={key} className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">
                                          {key}: {value}
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className="col-span-2">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${item.type === 'folder' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-700'
                                  }`}>
                                  {item.type === 'folder' ? 'Folder' : 'File'}
                                </span>
                              </div>
                              <div className="col-span-2">
                                <span className="text-sm text-gray-500">{item.lastModified || '-'}</span>
                              </div>
                              <div className="col-span-2">
                                <span className="text-sm text-gray-900">{item.size !== '0 B' ? item.size : '-'}</span>
                              </div>
                              <div className="col-span-1 flex justify-end gap-1">
                                {item.type === 'file' && (
                                  <>
                                    {isPreviewable(item) && (
                                      <button
                                        onClick={(e) => { e.stopPropagation(); setSelectedFile(item); handlePreview(); }}
                                        className="p-1 text-gray-400 hover:text-gray-600"
                                      >
                                        <Eye size={16} />
                                      </button>
                                    )}
                                    <button
                                      onClick={(e) => { e.stopPropagation(); setSelectedFile(item); handleDownload(); }}
                                      className="p-1 text-gray-400 hover:text-gray-600"
                                    >
                                      <Download size={16} />
                                    </button>
                                  </>
                                )}
                                <button
                                  onClick={(e) => { e.stopPropagation(); setSelectedFile(item); }}
                                  className="p-1 text-gray-400 hover:text-gray-600"
                                >
                                  <MoreVertical size={16} />
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  ) : (
                    /* Grid View */
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 p-4">
                      {items.map((item, index) => (
                        <div
                          key={`${item.name}-${index}`}
                          onClick={() => handleItemClick(item)}
                          className={`flex flex-col items-center p-4 rounded-lg border cursor-pointer transition-all hover:shadow-md ${selectedFile?.name === item.name
                            ? 'bg-indigo-50 border-indigo-300'
                            : 'bg-white border-gray-200 hover:border-gray-300'
                            }`}
                        >
                          <div className="mb-3">
                            {item.type === 'folder'
                              ? <Folder size={40} className="text-amber-500" />
                              : <File size={40} className="text-gray-400" />
                            }
                          </div>
                          <p className="text-sm font-medium text-gray-900 text-center truncate w-full">{item.name}</p>
                          <p className="text-xs text-gray-500 mt-1">{item.type === 'folder' ? 'Folder' : item.size}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            ) : activeTab === 'shared-with-me' ? (
              /* Shared with me - Empty State */
              <div className="flex flex-col items-center justify-center py-24">
                <div className="w-20 h-20 bg-indigo-100 rounded-full flex items-center justify-center mb-6">
                  <Share2 size={40} className="text-indigo-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No shared documents</h3>
                <p className="text-gray-500 text-center max-w-md mb-6">
                  When someone shares a document or folder with you, it will appear here.
                </p>
                <button className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors">
                  <Plus size={18} />
                  Request Access
                </button>
              </div>
            ) : activeTab === 'shared-with-others' ? (
              /* Shared with others - Empty State */
              <div className="flex flex-col items-center justify-center py-24">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-6">
                  <Share2 size={40} className="text-green-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No shared documents</h3>
                <p className="text-gray-500 text-center max-w-md mb-6">
                  Documents and folders you share with others will appear here.
                </p>
                <button
                  onClick={() => setActiveTab('folders')}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  <FolderOpen size={18} />
                  Browse My Folders
                </button>
              </div>
            ) : activeTab === 'secure' ? (
              /* Secure folder - Empty State */
              <div className="flex flex-col items-center justify-center py-24">
                <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mb-6">
                  <Lock size={40} className="text-amber-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Secure folder is empty</h3>
                <p className="text-gray-500 text-center max-w-md mb-6">
                  Store sensitive documents in your secure folder. Files here are encrypted and require additional authentication.
                </p>
                <button className="inline-flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors">
                  <Upload size={18} />
                  Upload Secure Document
                </button>
              </div>
            ) : activeTab === 'recycle' ? (
              /* Recycle Bin - Empty State */
              <div className="flex flex-col items-center justify-center py-24">
                <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-6">
                  <Recycle size={40} className="text-gray-500" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Recycle bin is empty</h3>
                <p className="text-gray-500 text-center max-w-md mb-6">
                  Items you delete will be moved here. You can restore or permanently delete them.
                </p>
                <button
                  onClick={() => setActiveTab('folders')}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                >
                  <Folder size={18} />
                  Go to My Folders
                </button>
              </div>
            ) : (
              /* All Activity Tab - Card-based Design */
              <>
                {/* Action Cards Row */}
                <div className="mb-8">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">What would you like to do?</h2>
                  <div className="grid grid-cols-4 gap-4">
                    {/* Create a folder */}
                    <button
                      onClick={() => setShowCreateFolderModal(true)}
                      className="flex items-center gap-4 p-4 bg-white rounded-xl border border-gray-200 hover:border-indigo-300 hover:shadow-sm transition-all text-left"
                    >
                      <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                        <FolderPlus size={20} className="text-indigo-600" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">Create a folder</p>
                        <p className="text-sm text-gray-500">Create a new document</p>
                      </div>
                    </button>

                    {/* Upload a document */}
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="flex items-center gap-4 p-4 bg-white rounded-xl border border-gray-200 hover:border-indigo-300 hover:shadow-sm transition-all text-left"
                    >
                      <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                        <Upload size={20} className="text-gray-600" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">Upload a document</p>
                        <p className="text-sm text-gray-500">Create a new document</p>
                      </div>
                    </button>

                    {/* Upload a folder */}
                    <button
                      onClick={() => setShowCreateBucketModal(true)}
                      className="flex items-center gap-4 p-4 bg-white rounded-xl border border-gray-200 hover:border-indigo-300 hover:shadow-sm transition-all text-left"
                    >
                      <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                        <FolderOpen size={20} className="text-gray-600" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">Upload a folder</p>
                        <p className="text-sm text-gray-500">Create a new document</p>
                      </div>
                    </button>

                    {/* Storage Usage */}
                    <div className="flex items-center gap-4 p-4 bg-white rounded-xl border border-gray-200">
                      <div className="flex-1">
                        <p className="text-2xl font-bold text-gray-900">{stats.totalSize}</p>
                        <p className="text-sm text-gray-500">of 15Gb used</p>
                        <button className="text-sm text-indigo-600 hover:text-indigo-700 font-medium mt-1">Expand</button>
                      </div>
                      {/* Mini pie chart */}
                      <div className="relative w-16 h-16">
                        <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                          <circle cx="50" cy="50" r="40" fill="transparent" stroke="#e5e7eb" strokeWidth="12" />
                          <circle
                            cx="50" cy="50" r="40" fill="transparent"
                            stroke="url(#storageGradient)" strokeWidth="12"
                            strokeDasharray={`${stats.usedPercentage * 2.51} 251`}
                            strokeLinecap="round"
                          />
                          <defs>
                            <linearGradient id="storageGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                              <stop offset="0%" stopColor="#10b981" />
                              <stop offset="50%" stopColor="#f59e0b" />
                              <stop offset="100%" stopColor="#f97316" />
                            </linearGradient>
                          </defs>
                        </svg>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Continue Section + Search */}
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-gray-900">Continue from where you stopped</h2>
                  <div className="relative">
                    <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search document list"
                      value={searchQuery}
                      onChange={(e) => handleSearch(e.target.value)}
                      className="pl-10 pr-4 py-2 w-64 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                    {searchQuery && (
                      <button onClick={handleClearSearch} className="absolute right-2 top-1/2 -translate-y-1/2">
                        <X size={14} className="text-gray-400" />
                      </button>
                    )}
                  </div>
                </div>

                {/* Folder Cards Grid */}
                <div className="grid grid-cols-4 gap-4 mb-8">
                  {contentLoading ? (
                    <>
                      {[1, 2, 3, 4, 5, 6, 7, 8].map(i => (
                        <div key={i} className="bg-white rounded-xl border border-gray-200 p-4 animate-pulse">
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-8 bg-gray-200 rounded"></div>
                              <div>
                                <div className="h-4 w-24 bg-gray-200 rounded mb-1"></div>
                                <div className="h-3 w-32 bg-gray-200 rounded"></div>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center justify-between">
                            <div className="h-3 w-16 bg-gray-200 rounded"></div>
                            <div className="flex -space-x-2">
                              {[1, 2, 3].map(j => (
                                <div key={j} className="w-6 h-6 bg-gray-200 rounded-full"></div>
                              ))}
                            </div>
                          </div>
                        </div>
                      ))}
                    </>
                  ) : folders.length === 0 && files.length === 0 ? (
                    <div className="col-span-4 flex flex-col items-center justify-center py-12 text-gray-500">
                      <Folder size={48} className="text-gray-300 mb-4" />
                      <p className="text-sm">No items found</p>
                    </div>
                  ) : (
                    <>
                      {/* Buckets as folder cards - limit to 8 total items */}
                      {buckets.slice(0, 8).map((bucket, index) => (
                        <div
                          key={bucket.name}
                          onClick={() => handleBucketSelect(bucket.name)}
                          className={`bg-white rounded-xl border p-4 cursor-pointer transition-all hover:shadow-md ${bucket.selected ? 'border-indigo-300 bg-indigo-50' : 'border-gray-200'
                            }`}
                        >
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center gap-3">
                              <div className="relative">
                                <Folder size={32} className={index % 4 === 0 ? 'text-blue-500' : index % 4 === 1 ? 'text-indigo-500' : index % 4 === 2 ? 'text-purple-500' : 'text-pink-500'} fill="currentColor" />
                                <div className={`absolute -bottom-1 -right-1 w-4 h-4 rounded-full ${index % 2 === 0 ? 'bg-indigo-600' : 'bg-orange-500'} flex items-center justify-center`}>
                                  <span className="text-white text-[8px]">−</span>
                                </div>
                              </div>
                              <div>
                                <p className="font-medium text-gray-900">{bucket.name}</p>
                                <p className="text-xs text-gray-500">Modified on, 07/01/2022</p>
                              </div>
                            </div>
                            <button className="p-1 hover:bg-gray-100 rounded">
                              <MoreVertical size={16} className="text-gray-400" />
                            </button>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-gray-500">{stats.filesCount} Files</span>
                            <div className="flex -space-x-2">
                              {[1, 2, 3, 4].map(i => (
                                <div key={i} className="w-6 h-6 rounded-full bg-indigo-200 border-2 border-white flex items-center justify-center">
                                  <span className="text-[10px] text-indigo-600 font-medium">{i}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      ))}

                      {/* Folders from current path - show remaining slots up to 8 total */}
                      {folders.slice(0, Math.max(0, 8 - buckets.length)).map((folder, index) => (
                        <div
                          key={folder.name}
                          onClick={() => handleItemClick(folder)}
                          className="bg-white rounded-xl border border-gray-200 p-4 cursor-pointer transition-all hover:shadow-md hover:border-gray-300"
                        >
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center gap-3">
                              <div className="relative">
                                <Folder size={32} className={index % 4 === 0 ? 'text-blue-500' : index % 4 === 1 ? 'text-indigo-500' : index % 4 === 2 ? 'text-purple-500' : 'text-pink-500'} fill="currentColor" />
                              </div>
                              <div>
                                <p className="font-medium text-gray-900 truncate max-w-[120px]">{folder.name}</p>
                                <p className="text-xs text-gray-500">Modified on, {folder.lastModified || '—'}</p>
                              </div>
                            </div>
                            <button className="p-1 hover:bg-gray-100 rounded" onClick={(e) => e.stopPropagation()}>
                              <MoreVertical size={16} className="text-gray-400" />
                            </button>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-gray-500">{folder.size}</span>
                            <div className="flex -space-x-2">
                              {[1, 2, 3, 4].map(i => (
                                <div key={i} className="w-6 h-6 rounded-full bg-indigo-200 border-2 border-white flex items-center justify-center">
                                  <span className="text-[10px] text-indigo-600 font-medium">{i}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      ))}
                    </>
                  )}
                </div>

                {/* Recently Uploaded Documents */}
                <div className="mb-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">Recently uploaded documents</h2>
                  <div className="grid grid-cols-4 gap-4">
                    {files.length === 0 ? (
                      <div className="col-span-4 flex flex-col items-center justify-center py-12 text-gray-500 bg-white rounded-xl border border-gray-200">
                        <FileText size={48} className="text-gray-300 mb-4" />
                        <p className="text-sm">No documents found</p>
                      </div>
                    ) : (
                      files.slice(0, 8).map((file) => (
                        <div
                          key={file.name}
                          onClick={() => handleItemClick(file)}
                          className={`bg-white rounded-xl border p-4 cursor-pointer transition-all hover:shadow-md ${selectedFile?.name === file.name ? 'border-indigo-300 bg-indigo-50' : 'border-gray-200'
                            }`}
                        >
                          {/* Document Preview */}
                          <div className="bg-gray-50 rounded-lg p-4 mb-3 h-40 flex items-center justify-center overflow-hidden">
                            <div className="text-center">
                              <div className="text-[10px] text-gray-400 uppercase tracking-wider mb-2">NON-DISCLOSURE</div>
                              <div className="text-[10px] text-gray-400 uppercase tracking-wider mb-4">AGREEMENT</div>
                              <div className="space-y-1">
                                {[1, 2, 3, 4, 5].map(i => (
                                  <div key={i} className="h-1 bg-gray-200 rounded" style={{ width: `${60 + Math.random() * 40}%`, marginLeft: 'auto', marginRight: 'auto' }}></div>
                                ))}
                              </div>
                            </div>
                          </div>

                          {/* File Info */}
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <div className={`w-6 h-6 rounded flex items-center justify-center text-white text-xs font-bold ${getFileIconColor(file.name)}`}>
                                {getFileTypeIcon(file.name)}
                              </div>
                              <span className="text-sm font-medium text-gray-900 truncate max-w-[120px]">{file.name}</span>
                            </div>
                            <button className="p-1 hover:bg-gray-100 rounded" onClick={(e) => { e.stopPropagation(); setSelectedFile(file); }}>
                              <MoreVertical size={16} className="text-gray-400" />
                            </button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          onChange={handleFileUpload}
        />

        {/* File Details Sidebar */}
        {selectedFile && selectedFile.type === 'file' && (
          <div className="fixed right-0 top-0 h-full w-96 bg-white border-l border-gray-200 shadow-lg z-50 flex flex-col">
            {/* Header */}
            <div className="p-4 border-b border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-semibold text-gray-900">File Details</h2>
                <div className="flex items-center gap-1">
                  <button className="p-1 hover:bg-gray-100 rounded">
                    <Maximize2 size={16} className="text-gray-500" />
                  </button>
                  <button onClick={() => setSelectedFile(null)} className="p-1 hover:bg-gray-100 rounded">
                    <X size={18} className="text-gray-500" />
                  </button>
                </div>
              </div>

              {/* File Preview/Icon */}
              <div className="flex flex-col items-center py-6 bg-gray-50 rounded-lg mb-4">
                <div className={`w-16 h-16 ${getFileIconColor(selectedFile.name)} rounded-lg flex items-center justify-center mb-3`}>
                  {getFileTypeIcon(selectedFile.name) === 'pdf' ? (
                    <FileText size={32} className="text-white" />
                  ) : (
                    <span className="text-2xl font-bold text-white">{getFileTypeIcon(selectedFile.name)}</span>
                  )}
                </div>
                <p className="font-medium text-gray-900 text-center px-4 break-all">{selectedFile.name}</p>
                <p className="text-xs text-gray-500 mt-1">{selectedFile.name.split('.').pop()?.toUpperCase()} File</p>
              </div>
            </div>

            {/* File Metadata */}
            <div className="flex-1 overflow-y-auto p-4">
              {/* Quick Info */}
              <div className="grid grid-cols-2 gap-3 mb-6">
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 mb-1">Size</p>
                  <p className="text-sm font-medium text-gray-900">{selectedFile.size || 'Unknown'}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 mb-1">Type</p>
                  <p className="text-sm font-medium text-gray-900">{selectedFile.name.split('.').pop()?.toUpperCase() || 'File'}</p>
                </div>
              </div>

              {/* Details Section */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-gray-900">Details</h3>

                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <Folder size={16} className="text-gray-400 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-gray-500">Location</p>
                      <p className="text-sm text-gray-900 truncate">{currentBucket}/{currentPath}</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <File size={16} className="text-gray-400 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-gray-500">File Name</p>
                      <p className="text-sm text-gray-900 break-all">{selectedFile.name}</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <HardDrive size={16} className="text-gray-400 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-gray-500">File Size</p>
                      <p className="text-sm text-gray-900">{selectedFile.size || 'Unknown'}</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <RefreshCw size={16} className="text-gray-400 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-gray-500">Last Modified</p>
                      <p className="text-sm text-gray-900">{selectedFile.lastModified || 'Unknown'}</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <Tag size={16} className="text-gray-400 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-gray-500">Content Type</p>
                      <p className="text-sm text-gray-900">{selectedFile.contentType || 'application/octet-stream'}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tags Section */}
              <div className="mt-6">
                <h3 className="text-sm font-semibold text-gray-900 mb-3">Tags</h3>
                <div className="flex flex-wrap gap-2">
                  <span className="px-2 py-1 bg-indigo-100 text-indigo-700 text-xs rounded-full">Document</span>
                  <button className="px-2 py-1 border border-dashed border-gray-300 text-gray-500 text-xs rounded-full hover:border-gray-400 hover:text-gray-600">
                    + Add tag
                  </button>
                </div>
              </div>
            </div>

            {/* Actions Footer */}
            <div className="p-4 border-t border-gray-200 space-y-2">
              <button onClick={handleDownload} className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors">
                <Download size={16} /> Download
              </button>
              <div className="grid grid-cols-2 gap-2">
                {isPreviewable(selectedFile) && (
                  <button onClick={handlePreview} className="flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 transition-colors">
                    <Eye size={16} /> Preview
                  </button>
                )}
                <button className="flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 transition-colors">
                  <Share2 size={16} /> Share
                </button>
              </div>
              <button onClick={() => setShowDeleteModal(true)} className="w-full flex items-center justify-center gap-2 px-4 py-2 border border-red-300 text-red-600 rounded-lg text-sm hover:bg-red-50 transition-colors">
                <Trash2 size={16} /> Delete
              </button>
            </div>
          </div>
        )}

        {/* Modals */}
        <CreateBucketModal
          isOpen={showCreateBucketModal}
          onClose={() => setShowCreateBucketModal(false)}
          onCreate={handleCreateBucket}
        />

        <CreateFolderModal
          isOpen={showCreateFolderModal}
          currentPath={breadcrumbs.map(b => b.label).join('/')}
          onClose={() => setShowCreateFolderModal(false)}
          onCreate={() => { }}
        />

        <DeleteConfirmModal
          isOpen={showDeleteModal}
          title="Delete File"
          message="Are you sure you want to delete this file?"
          itemName={selectedFile?.name || ''}
          isDeleting={isDeleting}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={handleDeleteFile}
        />

        <DeleteConfirmModal
          isOpen={showDeleteBucketModal}
          title="Delete Bucket"
          message="Are you sure you want to delete this bucket?"
          itemName={currentBucket}
          isDeleting={isDeleting}
          onClose={() => setShowDeleteBucketModal(false)}
          onConfirm={handleDeleteBucket}
        />

        <FilePreviewModal
          isOpen={showPreviewModal}
          fileName={selectedFile?.name || ''}
          fileUrl={previewUrl}
          isPdf={isPdfPreview}
          onClose={() => { setShowPreviewModal(false); setPreviewUrl(null); }}
          onDownload={handleDownload}
        />
      </div>
    </div>
  );
};

export default StoragePage;
